/**
 * File naming utilities for Class Insights exports
 * Format: class-insights-{section}-{date}.{ext}
 * Example: class-insights-comment-banks-2026-03-24.csv
 */

/**
 * Formats a date to YYYY-MM-DD format
 * @param {Date} date - The date to format
 * @returns {string} Formatted date string
 */
function formatDate(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Sanitize class name for filename
 * @param {string} className - Class name
 * @returns {string} Sanitized class name
 */
function sanitizeClassName(className) {
  if (!className) return '';
  return className
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Generate filename for per-card export
 * @param {string} section - Section name (e.g., 'comment-banks', 'success-criteria')
 * @param {string} extension - File extension (e.g., 'csv', 'xlsx', 'pdf')
 * @param {string} className - Optional class name
 * @returns {string} Complete filename
 */
export function generateSectionFilename(section, extension, className = null) {
  const date = formatDate();
  const normalizedSection = section.toLowerCase().replace(/\s+/g, '-');
  return `class-insights-${normalizedSection}-${date}.${extension}`;
}

/**
 * Generate filename for full report export
 * @param {string} extension - File extension (e.g., 'csv', 'xlsx', 'pdf')
 * @param {string} className - Optional class name
 * @returns {string} Complete filename
 */
export function generateReportFilename(extension, className = null) {
  const date = formatDate();
  return `class-insights-report-${date}.${extension}`;
}

/**
 * Section name constants
 */
export const SECTIONS = {
  COMMENT_BANKS: 'comment-banks',
  SUCCESS_CRITERIA: 'success-criteria',
  RUBRICS: 'rubrics',
  SHORT_ANSWER: 'short-answer',
  SUMMARY: 'summary',
};
